import { TControlName, TUpdaterName } from '@infotech-soe/metaschema';
import cloneDeep from 'lodash/cloneDeep';

import { TActionArgsValues } from '../../infrastructure/providers/abstract/IApiProvider';
import CategoryViewModel from '../category/view-model/CategoryViewModel';
import ContextService from '../context/ContextService';
import IContextService from '../context/IContextService';
import { DatasetCurrentState } from '../dataset/view-model/DatasetCurrentState';
import { DictionaryOutputs } from '../dictionary/IDictionaryService';
import UniqueId from '../uid/UniqueId';
import IUserConfigService from '../user-config/IUserConfigService';
import UserConfigService from '../user-config/UserConfigService';

import FormBuilder from './builders/FormBuilder';
import IFormService, { FormUpdateStrategy } from './IFormService';
import { isConditionForm } from './typeguards';
import FormUpdaterService from './updaters/FormUpdaterService';
import { FormControlViewModelType } from './view-model/FormControlViewModel';
import FormCreateViewModel from './view-model/FormCreateViewModel';
import { FormSourceType } from './view-model/FormSource';
import {
  FormUpdateViewModel,
  FormUpdateWithDictOutputViewModel,
  FormViewModel,
} from './view-model/FormViewModel';

class FormService implements IFormService {
  private readonly userConfig: IUserConfigService;
  private readonly contextService: IContextService;
  constructor() {
    this.userConfig = new UserConfigService();
    this.contextService = new ContextService();
  }

  public async create(
    createModel: FormCreateViewModel
  ): Promise<FormViewModel> {
    return new FormBuilder(createModel).build();
  }

  public clearForm(
    form: FormViewModel,
    category: CategoryViewModel,
    datasetsState: Record<UniqueId, DatasetCurrentState>
  ): Promise<FormUpdateViewModel> {
    return new FormUpdaterService(this.contextService).clearForm(
      form,
      category,
      datasetsState
    );
  }

  public update(
    form: FormViewModel,
    values: TActionArgsValues,
    strategy: FormUpdateStrategy
  ): Promise<FormUpdateViewModel> {
    return new FormUpdaterService(this.contextService).updateForm(
      form,
      values,
      strategy
    );
  }

  public updateWithOutParams(
    form: FormViewModel,
    controlName: TControlName,
    outParams: DictionaryOutputs,
    category: CategoryViewModel,
    datasetsState: Record<UniqueId, DatasetCurrentState>
  ): Promise<FormUpdateWithDictOutputViewModel> {
    Object.keys(outParams).forEach(key => {
      if (Array.isArray(outParams[key])) {
        outParams[key] = outParams[key].join(';');
      }
    });
    return new FormUpdaterService(this.contextService).updateWithOutParams(
      form,
      controlName,
      outParams,
      category,
      datasetsState
    );
  }

  public runFormSchemaUpdater(
    formState: FormViewModel,
    controlName: TControlName,
    value: any,
    category: CategoryViewModel,
    datasetsState: Record<UniqueId, DatasetCurrentState>
  ): Promise<FormUpdateViewModel> {
    return new FormUpdaterService(this.contextService).runFormSchemaUpdater(
      formState,
      controlName,
      value,
      category,
      datasetsState
    );
  }

  public runSingleUpdater(
    formState: FormViewModel,
    controlName: TControlName,
    category: CategoryViewModel,
    datasetsState: Record<UniqueId, DatasetCurrentState>,
    updaterName?: TUpdaterName
  ): Promise<FormUpdateViewModel> {
    return new FormUpdaterService(this.contextService).runSingleUpdater(
      formState,
      controlName,
      category,
      datasetsState,
      updaterName
    );
  }

  public async saveConfig(form: FormViewModel): Promise<void> {
    const { source, controls } = form;

    if (
      !isConditionForm(source) &&
      source.type !== FormSourceType.FORM &&
      source.type !== FormSourceType.PRINT
    ) {
      return;
    }

    const currentConfig = cloneDeep(source.formConfig);

    for (const [controlName, control] of Object.entries(controls)) {
      if (control.hasValue) {
        if (!currentConfig[controlName]) {
          currentConfig[controlName] = {};
        }
        const isValuable =
          control.value !== null &&
          control.value !== '' &&
          control.value !== undefined;
        if ('storeValue' in control && control.storeValue && isValuable) {
          currentConfig[controlName].value =
            control.type === FormControlViewModelType.DATE
              ? (control.value as Date).toUTCString()
              : control.value;
        } else {
          delete currentConfig[controlName].value;
        }

        if (control.type === FormControlViewModelType.DATASET) {
          currentConfig[controlName].columnsOrder = control.config.columnsOrder;
          currentConfig[controlName].columnsWidth = control.config.columnsWidth;
        }

        if (control.hasHistory) {
          const history = [...(control.history ?? [])];

          if (control.value) {
            const index = history.indexOf(control.value);
            if (index === -1 && history.length > 9) {
              history.pop();
            } else if (index > 0) {
              history.splice(index, 1);
            }

            if (index !== 0) {
              history.unshift(control.value);
            }
          }

          currentConfig[controlName].history = history;
        }
      }
      if (
        control.type === FormControlViewModelType.BOX &&
        control.collapsible
      ) {
        if (!currentConfig[controlName]) {
          currentConfig[controlName] = {};
        }
        currentConfig[controlName].collapsed = control.collapsed;
      }
    }

    await this.userConfig.saveFormConfig(
      source.category,
      source.form,
      currentConfig
    );
  }
}

export default FormService;
